import { AxiosProgressEvent } from 'axios';

import { baseClient } from 'src/app/baseClient';

import { WithPagination } from '../types/pagination';
import {
  ConfigurationStepInputs,
  CreateOrUpdateProgramPayload,
  InitialStepInputs,
  Instructor,
  Program,
  SpallaInfo,
} from '../types/program';
import { SimpleProgram, SimpleProgramParams } from '../types/simpleProgram';

// v2 endpoints
async function fetchProgramList(params?: SimpleProgramParams) {
  const query = new URLSearchParams({
    ...(params?.page && { page: params.page.toString() }),
    ...(params?.limit && { limit: params.limit.toString() }),
    ...(params?.createdAt && { createdAt: params.createdAt }),
    ...(params?.status && { status: params.status }),
    ...(params?.text && { text: params.text }),
  });
  const queryString = query.toString();

  const endpoint = `/v2/programs${queryString ? `?${queryString}` : ''}`;

  const { data } = await baseClient.get<WithPagination<SimpleProgram[]>>(
    endpoint
  );
  return data;
}

async function fetchInitialStepInputs() {
  const { data } = await baseClient.get<InitialStepInputs>(
    `/v2/programs/inputs/home`
  );
  return data;
}

async function fetchConfigurationStepInputs() {
  const { data } = await baseClient.get<ConfigurationStepInputs>(
    `/v2/programs/inputs/configuration`
  );
  return data;
}

async function fetchInstructors() {
  const { data } = await baseClient.get<Pick<Instructor, 'id' | 'name'>[]>(
    `/v2/programs/inputs/instructors`
  );
  return data;
}

async function fetchInstructor(instructorId?: number) {
  const { data } = await baseClient.get(
    `/v2/programs/instructors/${instructorId}`
  );

  return data;
}

async function createInstructor(instructorData: FormData) {
  return await baseClient.post(`/v2/programs/instructors`, instructorData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

async function createProgram(name: string) {
  const { data } = await baseClient.post<{ id: number }>(`/v2/programs`, {
    name,
  });

  return data.id;
}

async function updateProgram(params: Partial<CreateOrUpdateProgramPayload>) {
  const { id, ...payload } = params;
  return await baseClient.put(`/v2/programs/${id}`, payload);
}

async function updatePublishedProgram(
  params: Partial<CreateOrUpdateProgramPayload>
) {
  const { id, ...payload } = params;
  return await baseClient.put(`/v2/programs/${id}/published`, payload);
}

async function fetchProgram(params: { id: number }) {
  const { data } = await baseClient.get<Program>(`/v2/programs/${params.id}`);
  return data;
}

async function publishProgram(programId: number) {
  return await baseClient.patch(`/v2/programs/${programId}/publish`);
}

async function unpublishProgram(programId: number) {
  return await baseClient.patch(`/v2/programs/${programId}/unpublish`);
}

async function uploadContentsFile(
  file: FormData,
  programId: number,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
) {
  const { data } = await baseClient.post<{ imageUrl: string }>(
    `/v2/programs/${programId}/module/contents/upload`,
    file,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }
  );

  return data;
}

async function uploadProgramFile(
  file: FormData,
  programId: number,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
) {
  const { data } = await baseClient.post<{ imageUrl: string }>(
    `/v2/programs/${programId}/upload`,
    file,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }
  );

  return data;
}

async function fetchSpallaInfo(spallaId: string) {
  const { data } = await baseClient.get<SpallaInfo>(`/spalla/${spallaId}`);
  return data;
}

export const programsApi = {
  fetchProgramList,
  fetchInitialStepInputs,
  fetchConfigurationStepInputs,
  fetchInstructors,
  fetchInstructor,
  createInstructor,
  createProgram,
  updateProgram,
  updatePublishedProgram,
  fetchProgram,
  publishProgram,
  unpublishProgram,
  uploadContentsFile,
  uploadProgramFile,
  fetchSpallaInfo,
};
